import { CONTEXT_TYPES } from '@dh-io-cscs/user-context-manager/dist/common/constants';
import FilterModel, { IProductFilterType } from '../../models/product-filter/product-filter.model';
import {
  b2xSuffixRegex,
  convertPriceFilterCode,
  convertPriceSortCode,
  dynamicPriceFilters,
  filterB2xSpecificPriceSortItems,
  isB2xPriceFilter,
  isB2xPriceSortCode,
  isPriceFilter,
} from '../../utils/dynamic-pricing.util';
import { IDealerContext } from '../../models/dealer.model';
import Vehicle from '../../models/vehicle.model';
import { HttpRequestConfig, HttpService } from '../../utils/http.util';
import { Logger } from '../../utils/logger.util';
import {
  FilterCode,
  IProductFilterDTO,
  IProductFilterPaginationDTO,
} from '../../models/product-filter/product-filter-base.model';
import { SMSProductSearchPage } from '@dh-io-emh/commerce-api/src/models/shared/ocb/api.schema';
import { ProductSearchResponse } from './product.api.types';
import { AppConfig } from '@dh-io-emh/config-types';

type ProductSearchResponseDTO = Required<SMSProductSearchPage>;

export type SearchProductParams = {
  selectedSort: string;
  selectedCategory: string;
  filters: IProductFilterType[];
  paging?: IProductFilterPaginationDTO;
  dealerContext?: IDealerContext;
  userContext?: CONTEXT_TYPES | string;
};

export type ResponseData = Pick<ProductSearchResponseDTO, 'pagination' | 'sorts'> & {
  facets: (IProductFilterDTO & { stats: any })[];
  products: Vehicle[];
  filters: IProductFilterType[];
  searchStats?: any;
};

export const parseSearchProductsRequest = async (
  searchProductsParams: SearchProductParams,
  config: AppConfig,
  { httpService, requestUrl }: { httpService: HttpService; requestUrl: string },
) => {
  const { selectedSort, selectedCategory, filters = [], paging, dealerContext, userContext } = searchProductsParams;
  const {
    shopCountry,
    filterWhitelist,
    sortWhitelist,
    srp: { availableCategories, enableDynamicPriceFiltering },
  } = config;
  const requestOptions = createSearchRequestOptions({
    shopCountry,
    selectedSort,
    selectedCategory,
    filters,
    paging,
    enableDynamicPriceFiltering,
    dealerContext,
    userContext,
  });

  const response: ProductSearchResponse = (await httpService.get<ProductSearchResponse>(requestUrl, requestOptions))
    ?.data;

  const { filterBlacklist, sortBlacklist } =
    availableCategories.find(({ name }) => name === selectedCategory) ?? availableCategories[0];

  return parseSearchRequestResponse({
    filterBlacklist,
    filterWhitelist,
    sortBlacklist,
    sortWhitelist,
    enableDynamicPriceFiltering,
    dealerContext,
    userContext,
    response,
  });
};

export const createSearchRequestOptions = ({
  shopCountry,
  enableDynamicPriceFiltering,
  selectedSort,
  selectedCategory,
  filters = [],
  paging,
  dealerContext,
  userContext,
}: {
  shopCountry: string;
  enableDynamicPriceFiltering: boolean;
  selectedSort: string;
  selectedCategory: string;
  filters: IProductFilterType[];
  paging?: IProductFilterPaginationDTO;
  dealerContext?: IDealerContext;
  userContext?: CONTEXT_TYPES | string;
}): HttpRequestConfig => {
  if (enableDynamicPriceFiltering) {
    selectedSort = convertPriceSortCode(selectedSort, userContext);
  }

  let query = `:${selectedSort}`;

  // Convert user friendly category name to backend friendly category param
  selectedCategory = `market-${shopCountry.toLowerCase()}-${selectedCategory}`;
  query += `:allCategories:${selectedCategory}`;

  filters.forEach((filter) => {
    if (filter.isSelected()) {
      // Ignore locations filter if there is a dealer context
      if (dealerContext && filter.code === FilterCode.locations) return;

      if (enableDynamicPriceFiltering && isPriceFilter(filter.code)) {
        // Convert price filter code to corresponding b2x filter code
        // We have to clone the filter as to not mutate the filter object coming from the store
        const filterClone = filter.clone();

        filterClone.code = convertPriceFilterCode(filter.code, userContext);
        query += filterClone.getQueryValue();
      } else {
        query += filter.getQueryValue();
      }
    }
  });

  const locationsFilter = filters.find(({ code }) => code === FilterCode.locations);

  if (dealerContext && locationsFilter) {
    try {
      dealerContext.allOutletId?.split(',').forEach((outlet) => {
        query += `:locations:${outlet}`;
      });
    } catch (err) {
      Logger.error('DealerContext -> invalid "dealerContext.allOutletId" property');
    }
  }

  return {
    params: {
      query,
      currentPage: paging?.currentPage || 0,
      pageSize: paging?.pageSize || 0,
      fields: 'FULL',
    },
    id: 'vehicle-search',
    cancelable: true,
  };
};

export const parseSearchRequestResponse = ({
  filterBlacklist,
  filterWhitelist,
  sortBlacklist,
  sortWhitelist,
  enableDynamicPriceFiltering,
  userContext,
  dealerContext,
  response,
}: {
  filterBlacklist: Record<string, boolean>;
  filterWhitelist: Record<string, boolean>;
  sortBlacklist: Record<string, boolean>;
  sortWhitelist: Record<string, boolean>;
  enableDynamicPriceFiltering: boolean;
  userContext?: CONTEXT_TYPES | string;
  dealerContext?: IDealerContext;
  response: ResponseData;
}): ResponseData => {
  let sorts = (response.sorts || []).filter(({ code }) => code && sortWhitelist[code] && !sortBlacklist[code]);

  let facets = (response.facets || []).filter(
    ({ visible, code }) =>
      visible && code !== FilterCode.allCategories && filterWhitelist[code] && !filterBlacklist[code],
  );

  if (enableDynamicPriceFiltering) {
    sorts = sorts.filter(({ code }) => filterB2xSpecificPriceSortItems(code as string, userContext as CONTEXT_TYPES));
    sorts.forEach((sort) => {
      if (isB2xPriceSortCode(sort.code as string)) {
        // Remove b2x suffix
        sort.code = sort.code?.replace(b2xSuffixRegex, '');
      }
    });

    facets = facets.filter((filter) => dynamicPriceFilters(userContext as CONTEXT_TYPES, filter));
    facets.forEach((filter) => {
      if (isPriceFilter(filter.code)) {
        // Convert filter code to non-b2x filter code
        filter.code = convertPriceFilterCode(filter.code);
      }
    });
  } else {
    // Remove all b2x specific filter and sort codes
    sorts = sorts.filter(({ code }) => !isB2xPriceSortCode(code as string));
    facets = facets.filter(({ code }) => !isB2xPriceFilter(code));
  }

  const responseData: ResponseData = {
    facets: [],
    pagination: response.pagination,
    sorts,
    products: [],
    filters: [],
  };

  // Match search stat values with corresponding filters and add corresponding attributes
  if (response.searchStats) {
    response.searchStats.forEach((stats: any) => {
      const matchedFilterIndex = facets.findIndex((filter) => filter.code === stats.name);

      if (matchedFilterIndex !== -1) {
        facets[matchedFilterIndex] = { ...facets[matchedFilterIndex], stats };
      }
    });
  }

  // Parse attributes into models
  responseData.products = (response.products || []).map((product) => Vehicle.create(product as any));
  responseData.filters = facets.map((filter) =>
    filter.code === FilterCode.locations && dealerContext
      ? FilterModel.create({ ...filter, visible: false })
      : FilterModel.create(filter),
  );

  // Order filters by priority since backend as inconsistent ordering
  responseData.filters = responseData.filters.sort((a, b) =>
    a.priority < b.priority ? 1 : a.priority > b.priority ? -1 : 0,
  );

  return responseData;
};
